/**
 * YagiChatBot の企業 URL 抽出フロー。
 */
import axios from 'axios'
import { nextTick, type ComputedRef, type Ref } from 'vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import {
  collectCompanyAutofill,
  fieldValueAsString,
  trackYagiChatGtag,
  type CompanyExtractResponse,
  type YagiChatFormValue,
  type YagiChatMessage,
  type YagiChatQuestion
} from '@/utils/format-edit/yagiChatBot'

export type YagiChatCompanyDeps = {
  questions: ComputedRef<YagiChatQuestion[]>
  value: ComputedRef<YagiChatFormValue>
  emitInput: (value: YagiChatFormValue) => void
  currentQuestionIndex: Ref<number>
  chatMessages: Ref<YagiChatMessage[]>
  currentAnswer: Ref<string>
  autoFilledFields: Ref<string[]>
  aiResultMode: Ref<boolean>
  aiResultCallbacks: Ref<unknown>
  isLoadingCompanyInfo: Ref<boolean>
  addQuestionToChat: (index: number) => void
  moveToNextUnskippedQuestion: (filledFields: string[]) => void
  scrollChatToBottom: () => void
}

export function useYagiChatBotCompany(deps: YagiChatCompanyDeps) {
  const { varuna } = useHostServices()

  function skipUrlQuestion() {
    deps.chatMessages.value.push({
      type: 'answer',
      text: 'スキップしました'
    })

    trackYagiChatGtag('ai_company_url_extract', {
      success: false,
      error: 'skipped'
    })

    deps.currentQuestionIndex.value++
    deps.currentAnswer.value = ''

    if (deps.currentQuestionIndex.value < deps.questions.value.length) {
      deps.addQuestionToChat(deps.currentQuestionIndex.value)
      const nextQuestion = deps.questions.value[deps.currentQuestionIndex.value]
      deps.currentAnswer.value = fieldValueAsString(
        deps.value.value,
        nextQuestion.field
      )
    }

    nextTick(() => deps.scrollChatToBottom())
  }

  function skipAutoFilledQuestions(filledFields: string[]) {
    deps.moveToNextUnskippedQuestion(filledFields)
    nextTick(() => deps.scrollChatToBottom())
  }

  async function submitUrlAnswer() {
    const currentQuestion =
      deps.questions.value[deps.currentQuestionIndex.value]
    const url = deps.currentAnswer.value.trim()

    if (!url) {
      skipUrlQuestion()
      return
    }

    deps.chatMessages.value.push({
      type: 'answer',
      text: url
    })

    deps.emitInput({
      ...deps.value.value,
      [currentQuestion.field]: url
    })

    deps.isLoadingCompanyInfo.value = true

    try {
      const response = await axios.post<CompanyExtractResponse>(
        varuna.endPoints.aiExtractCompanyInfo,
        { url }
      )

      if (response.data.success && response.data.data) {
        const companyData = response.data.data
        const autoFillFields = currentQuestion.autoFillFields || {}
        const { updatedValue, filledFields } = collectCompanyAutofill(
          companyData,
          autoFillFields,
          deps.value.value
        )

        deps.emitInput(updatedValue)
        deps.autoFilledFields.value = filledFields

        deps.chatMessages.value.push({
          type: 'company-info',
          text: '企業情報を取得しました',
          companyData,
          editableFields: filledFields
        })

        trackYagiChatGtag('ai_company_url_extract', {
          success: true,
          fields_filled: filledFields.length,
          has_company_name: !!companyData.company_name,
          has_industry: !!companyData.industry,
          has_job: !!companyData.job,
          has_values: !!companyData.values
        })

        deps.currentQuestionIndex.value++
        deps.currentAnswer.value = ''
        skipAutoFilledQuestions(filledFields)
      } else {
        trackYagiChatGtag('ai_company_url_extract', {
          success: false,
          error: response.data.error || 'unknown_error'
        })

        deps.chatMessages.value.push({
          type: 'system',
          text:
            response.data.error ||
            '企業情報を取得できませんでした。手動で入力してください。'
        })
        deps.currentQuestionIndex.value++
        deps.currentAnswer.value = ''
        if (deps.currentQuestionIndex.value < deps.questions.value.length) {
          deps.addQuestionToChat(deps.currentQuestionIndex.value)
        }
      }
    } catch (error) {
      console.error('企業情報取得エラー:', error)
      trackYagiChatGtag('ai_company_url_extract', {
        success: false,
        error: 'exception'
      })
      deps.chatMessages.value.push({
        type: 'system',
        text: '企業情報の取得に失敗しました。手動で入力してください。'
      })
      deps.currentQuestionIndex.value++
      deps.currentAnswer.value = ''
      if (deps.currentQuestionIndex.value < deps.questions.value.length) {
        deps.addQuestionToChat(deps.currentQuestionIndex.value)
      }
    } finally {
      deps.isLoadingCompanyInfo.value = false
      nextTick(() => deps.scrollChatToBottom())
    }
  }

  function editCompanyInfo(editableFields: string[]) {
    deps.autoFilledFields.value = []
    deps.aiResultMode.value = false
    deps.aiResultCallbacks.value = null

    for (let i = 0; i < deps.questions.value.length; i++) {
      if (editableFields.includes(deps.questions.value[i].field)) {
        deps.currentQuestionIndex.value = i
        deps.chatMessages.value = []
        deps.addQuestionToChat(i)
        const currentQuestion = deps.questions.value[i]
        deps.currentAnswer.value = fieldValueAsString(
          deps.value.value,
          currentQuestion.field
        )
        break
      }
    }
  }

  return {
    skipUrlQuestion,
    submitUrlAnswer,
    editCompanyInfo
  }
}
